import moment from "moment";
import { useMemo, useState } from "react";
import {
  getMonthsWeekData,
  getWeekRange,
} from "../../utils/annual-planner-helpers";

// Drives the week stepper (prev/next/this-week) and the Weekly Notes panel
// below it. Shares getWeekRange's rangeFirstDay with useListViewWeeks so
// "week 0" starts on the same day in both places, but clamps against
// getMonthsWeekData's count instead of getWeekRange's own - the stepper and
// Notes panel sit below the Calendar grid, so "last/first visible week" has
// to mean what that grid actually renders, not List view's own (sometimes
// longer) week count (see getMonthsWeekData's comment).
export const useWeekSelection = ({ dateFromState, startingCalendarDay }) => {
  const { rangeFirstDay } = useMemo(
    () => getWeekRange(dateFromState, startingCalendarDay),
    [dateFromState, startingCalendarDay]
  );
  const numberOfWeeks = useMemo(
    () =>
      getMonthsWeekData(dateFromState, startingCalendarDay).reduce(
        (total, { numberOfWeeks: monthWeeks }) => total + monthWeeks,
        0
      ),
    [dateFromState, startingCalendarDay]
  );

  const clampIndex = (index) => Math.min(Math.max(index, 0), numberOfWeeks - 1);

  // Same day-diff/floor convention bucketByWeek uses, so "this week" lands
  // on the identical week index the table would bucket today's data into.
  const weekIndexOf = (date) =>
    clampIndex(
      Math.floor(moment(date).startOf("day").diff(rangeFirstDay, "days") / 7)
    );

  // Kept as a date (not an index) so moving the 12-month window keeps the
  // same week selected while it is still visible, and clamps to the nearest
  // edge week once it scrolls out.
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const clampedIndex = weekIndexOf(selectedDate);
  const selectedWeek = moment(rangeFirstDay).add(clampedIndex, "weeks");

  // Bumped on every selectWeek call (never on mount, never from the stepper)
  // so WeekNotesPanel knows a click elsewhere - not just prev/next/this-week
  // - just aimed it at a new week, and should scroll into view + focus the
  // textarea instead of silently updating off-screen.
  const [focusSignal, setFocusSignal] = useState(0);

  // Lets a click elsewhere (a calendar week's note bubble, a List view row,
  // a footer comment) jump the panel straight to that week - same
  // day-diff/floor convention as weekIndexOf above, so it always lands on
  // the exact week clicked. `silent` skips the focusSignal bump - used by
  // the Week of date button, which only marks the week as selected in the
  // grid and shouldn't also yank the page down to the notes panel.
  const selectWeek = (week, { silent = false } = {}) => {
    setSelectedDate(moment(week));
    if (!silent) setFocusSignal((signal) => signal + 1);
  };

  return {
    selectedWeek,
    selectWeek,
    focusSignal,
    goToPreviousWeek: () =>
      setSelectedDate(moment(selectedWeek).subtract(1, "week")),
    goToNextWeek: () => setSelectedDate(moment(selectedWeek).add(1, "week")),
    goToThisWeek: () => setSelectedDate(moment()),
    hasPreviousWeek: clampedIndex > 0,
    hasNextWeek: clampedIndex < numberOfWeeks - 1,
  };
};
